// Timeline View Component

import React, { useState } from 'react';
import Button from 'devextreme-react/button';
import Sortable from 'devextreme-react/sortable';
import { getVehicleColor } from './map-utils';

interface TimelineViewProps {
  routes: any[];
  loading: boolean;
  onOptimizeWithAI: (route: any) => Promise<void>;
  onSetSelectedRoute: (route: any) => void;
  onSetRoutes: (routes: any[]) => void;
  showToast: (type: 'success' | 'error' | 'info' | 'warning', message: string) => void;
}

export const TimelineView: React.FC<TimelineViewProps> = ({
  routes,
  loading,
  onOptimizeWithAI,
  onSetSelectedRoute,
  onSetRoutes,
  showToast
}) => {
  const [ignoreDates, setIgnoreDates] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);

  const handleZoomIn = () => setZoomLevel(prev => Math.min(prev + 0.2, 2));
  const handleZoomOut = () => setZoomLevel(prev => Math.max(prev - 0.2, 0.5));
  const handleZoomAll = () => setZoomLevel(1);

  if (routes.length === 0) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: '#f8f9fa'
      }}>
        {/* Toolbar */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          padding: '8px 16px',
          background: '#ffffff',
          borderBottom: '1px solid #e2e8f0',
          gap: '8px'
        }}>
          <Button icon="plus" stylingMode="outlined" disabled />
          <Button icon="minus" stylingMode="outlined" disabled />
        </div>

        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: '16px',
          color: '#94a3b8'
        }}>
          <i className="dx-icon dx-icon-event" style={{ fontSize: '64px' }}></i>
          <div style={{ fontSize: '16px', fontWeight: '500' }}>Henüz hesaplanmış rota bulunmuyor</div>
          <div style={{ fontSize: '13px', color: '#cbd5e1' }}>Rotayı hesapla butonuna tıklayarak başlayın</div>
        </div>
      </div>
    );
  }

  // Find min and max times from all routes to determine timeline range
  let minTime = Infinity;
  let maxTime = 0;

  routes.forEach((route: any) => {
    // Use route start_time
    if (route.start_time !== undefined) {
      minTime = Math.min(minTime, route.start_time);
    }

    // Calculate actual end time from route_sequence
    if (route.route_sequence && Array.isArray(route.route_sequence)) {
      let currentTime = route.start_time || 0;

      route.route_sequence.forEach((step: any, stepIndex: number) => {
        const locationType = step.location_type?.toUpperCase();

        if (locationType === 'DEPOT') {
          if (stepIndex === 0) {
            currentTime = step.end_time || step.arrival_time || currentTime;
          } else {
            // Son depot - önceki + yolculuk süresi
            const travelTime = step.valhalla_time_seconds || 0;
            currentTime = currentTime + travelTime;
            maxTime = Math.max(maxTime, currentTime);
          }
        } else if (locationType === 'CUSTOMER' || locationType === 'BREAK') {
          const serviceDuration = step.service_duration || 0;
          const travelTime = step.valhalla_time_seconds || 0;
          currentTime = currentTime + travelTime + serviceDuration;
          maxTime = Math.max(maxTime, currentTime);
        }
      });
    }
  });

  // If no valid times found, default to full day
  if (!isFinite(minTime)) {
    minTime = 0;
  }
  if (maxTime === 0) {
    maxTime = 86400;
  }

  // Round to hour boundaries for cleaner display
  const startHour = Math.floor(minTime / 3600);
  const endHour = Math.ceil(maxTime / 3600);

  const timelineStart = startHour * 3600; // Round down to hour
  const timelineEnd = endHour * 3600; // Round up to hour

  // Determine time slot interval based on zoom level
  let timeInterval: number;
  if (zoomLevel <= 0.7) {
    timeInterval = 7200; // 2 hours
  } else if (zoomLevel <= 1.3) {
    timeInterval = 3600; // 1 hour
  } else if (zoomLevel <= 1.7) {
    timeInterval = 1800; // 30 minutes
  } else {
    timeInterval = 900; // 15 minutes
  }

  // Generate time slots
  const timeSlots: { timeInSeconds: number; hour: number; minute: number; label: string }[] = [];
  for (let t = timelineStart; t <= timelineEnd; t += timeInterval) {
    const totalHours = Math.floor(t / 3600);
    const h = totalHours % 24;
    const m = Math.floor((t % 3600) / 60);
    timeSlots.push({
      timeInSeconds: t,
      hour: h,
      minute: m,
      label: `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`
    });
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      background: '#f8f9fa'
    }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        padding: '8px 16px',
        background: '#ffffff',
        borderBottom: '1px solid #e2e8f0',
        gap: '8px'
      }}>
        <Button
          icon="plus"
          stylingMode="outlined"
          onClick={handleZoomIn}
        />
        <Button
          icon="minus"
          stylingMode="outlined"
          onClick={handleZoomOut}
        />
      </div>

      {/* Timeline Grid */}
      <div style={{
        flex: 1,
        overflow: 'auto',
        position: 'relative'
      }}>
        {/* Time Header */}
        <div style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          display: 'flex',
          background: '#ffffff',
          borderBottom: '2px solid #cbd5e1'
        }}>
          {/* Empty corner for vehicle column */}
          <div style={{
            width: '60px',
            minWidth: '60px',
            background: '#f1f5f9',
            borderRight: '1px solid #e2e8f0'
          }}></div>

          {/* Time cells */}
          <div style={{
            display: 'flex',
            flex: 1,
            minWidth: `${timeSlots.length * 80 * zoomLevel}px`
          }}>
            {timeSlots.map((slot, idx) => (
              <div
                key={`slot-${idx}`}
                style={{
                  width: `${80 * zoomLevel}px`,
                  minWidth: `${80 * zoomLevel}px`,
                  padding: '8px 4px',
                  fontSize: '11px',
                  color: '#64748b',
                  borderRight: '1px solid #e2e8f0',
                  textAlign: 'center',
                  fontWeight: slot.minute === 0 ? '600' : '500'
                }}
              >
                {slot.label}
              </div>
            ))}
          </div>
        </div>

        {/* Timeline Rows */}
        {routes.map((route: any, routeIndex: number) => {
          // Extract numeric part from vehicle_id (e.g., "vehicle_020" -> 20)
          let vehicleIdNum = route.vehicle_id;
          if (typeof vehicleIdNum === 'string') {
            const match = vehicleIdNum.match(/\d+/);
            vehicleIdNum = match ? parseInt(match[0], 10) : parseInt(vehicleIdNum, 10);
          }
          const vehicleColor = getVehicleColor(vehicleIdNum);

          // Build blocks from route_sequence
          const blocks: any[] = [];

          if (route.route_sequence && Array.isArray(route.route_sequence)) {
            let customerCount = 0;
            let previousEndTime = route.start_time || 0;

            route.route_sequence.forEach((step: any, stepIndex: number) => {
              const locationType = step.location_type?.toUpperCase();

              if (locationType === 'DEPOT') {
                // Başlangıç deposu için end_time, son depot için arrival_time + travelTime
                let depotTime: number;

                if (stepIndex === 0) {
                  // İlk depot (başlangıç)
                  depotTime = step.end_time || step.arrival_time;
                } else {
                  // Son depot - önceki bitiş + yolculuk süresi
                  const travelTime = step.valhalla_time_seconds || 0;
                  depotTime = previousEndTime + travelTime;
                }

                if (depotTime === undefined) return;

                blocks.push({
                  startTime: depotTime,
                  endTime: depotTime,
                  type: 'DEPOT',
                  name: step.name || 'Depo',
                  color: vehicleColor,
                  customerNumber: null,
                  isMarker: true
                });

                previousEndTime = depotTime;
                return;
              }

              if (locationType === 'CUSTOMER' || locationType === 'BREAK') {
                const serviceDuration = step.service_duration || 0;
                const travelTime = step.valhalla_time_seconds || 0;

                // Gerçek varış zamanı = önceki bitiş + yolculuk süresi
                const actualArrival = previousEndTime + travelTime;

                // Servis başlangıç ve bitiş
                const serviceStart = actualArrival;
                const serviceEnd = actualArrival + serviceDuration;

                // Increment customer count for numbering
                if (locationType === 'CUSTOMER') {
                  customerCount++;
                }

                blocks.push({
                  startTime: serviceStart,
                  endTime: serviceEnd,
                  type: locationType,
                  name: step.customer_name || step.name || locationType,
                  color: vehicleColor,
                  customerNumber: locationType === 'CUSTOMER' ? customerCount : null,
                  isMarker: false
                });

                previousEndTime = serviceEnd;
              }
            });
          }

          // Convert hex color to rgba with low opacity for row background
          const hexToRgba = (hex: string, alpha: number) => {
            const r = parseInt(hex.slice(1, 3), 16);
            const g = parseInt(hex.slice(3, 5), 16);
            const b = parseInt(hex.slice(5, 7), 16);
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
          };

          const rowBgColor = vehicleColor ? hexToRgba(vehicleColor, 0.15) : '#ffffff';

          return (
            <div
              key={route.vehicle_id}
              style={{
                display: 'flex',
                borderBottom: '1px solid #e2e8f0',
                minHeight: '40px',
                background: rowBgColor
              }}
            >
              {/* Vehicle Type Column */}
              <div style={{
                width: '60px',
                minWidth: '60px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: vehicleColor ? hexToRgba(vehicleColor, 0.25) : '#f8fafc',
                borderRight: '1px solid #e2e8f0',
                fontWeight: '600',
                fontSize: '11px',
                color: vehicleColor || '#1e293b',
                textAlign: 'center',
                padding: '4px',
                lineHeight: '1.2'
              }}>
                {route.vehicle_type || route.vehicle_id}
              </div>

              {/* Timeline Track */}
              <div style={{
                position: 'relative',
                flex: 1,
                minWidth: `${timeSlots.length * 80 * zoomLevel}px`,
                height: '40px'
              }}>
                {/* Grid lines */}
                {timeSlots.map((slot, idx) => {
                  const isFullHour = slot.minute === 0;
                  return (
                    <div
                      key={`grid-${idx}`}
                      style={{
                        position: 'absolute',
                        left: `${idx * 80 * zoomLevel}px`,
                        top: 0,
                        bottom: 0,
                        width: isFullHour ? '2px' : '1px',
                        background: isFullHour ? '#cbd5e1' : '#f1f5f9'
                      }}
                    />
                  );
                })}

                {/* Timeline Blocks */}
                {blocks.map((block, blockIdx) => {
                  // Calculate position: pixels per second
                  const totalDuration = timelineEnd - timelineStart;
                  const totalWidth = timeSlots.length * 80 * zoomLevel;
                  const pixelsPerSecond = totalWidth / totalDuration;

                  const startOffset = (block.startTime - timelineStart) * pixelsPerSecond;
                  const blockDuration = (block.endTime - block.startTime) * pixelsPerSecond;

                  // Format time display
                  const formatTime = (seconds: number) => {
                    const h = Math.floor(seconds / 3600) % 24;
                    const m = Math.floor((seconds % 3600) / 60);
                    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
                  };
                  const startTimeStr = formatTime(block.startTime);
                  const endTimeStr = formatTime(block.endTime);

                  // Render depot as an icon
                  if (block.isMarker) {
                    return (
                      <div
                        key={blockIdx}
                        style={{
                          position: 'absolute',
                          left: `${startOffset - 12}px`,
                          width: '24px',
                          height: '24px',
                          top: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          background: block.color || '#64748b',
                          borderRadius: '4px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                        }}
                        title={`${block.name}\n${startTimeStr}`}
                      >
                        <i
                          className="dx-icon dx-icon-home"
                          style={{
                            fontSize: '14px',
                            color: '#ffffff'
                          }}
                        ></i>
                      </div>
                    );
                  }

                  // Regular blocks for customers and breaks
                  return (
                    <div
                      key={blockIdx}
                      style={{
                        position: 'absolute',
                        left: `${startOffset}px`,
                        width: `${Math.max(blockDuration, 20)}px`,
                        top: '4px',
                        height: '32px',
                        background: block.color || '#3b82f6',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '11px',
                        color: '#ffffff',
                        fontWeight: '600',
                        cursor: 'pointer',
                        overflow: 'hidden',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                      }}
                      title={`${block.name}\n${startTimeStr} - ${endTimeStr}`}
                    >
                      {blockDuration > 40 && block.customerNumber && (
                        <span style={{ padding: '0 8px', whiteSpace: 'nowrap' }}>{block.customerNumber}</span>
                      )}
                      {blockDuration > 40 && block.type === 'BREAK' && (
                        <span style={{ padding: '0 8px', whiteSpace: 'nowrap' }}>M</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
